/**
 * 팝업보기 (2026-08-21) — 메일을 따로 띄워 읽는다.
 *
 * ★ 새 브라우저 창(`window.open`)이 아니라 **앱 안에 떠 있는 창**이다. 설치형에서 새 창을
 *   띄우면 껍데기만 열리고 세션·테마가 따라가지 않는다(설정 모달에서 이미 겪었다).
 * ★ 본문은 상세 패널을 **그대로 재사용**한다 — 두 벌이 되면 한쪽만 고쳐지는 날이 온다.
 *
 * 머리줄을 끌어 옮길 수 있고, 화면 밖으로는 나가지 않는다.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Mail, X } from '../../icons';
import { tMail } from '../../lib/mail-i18n';
import { MailDetail } from './MailDetail';
import type { ComposePrefill } from './ComposeModal';

interface MailPopoutProps {
  mailId: string | null;
  title: string;
  onClose: () => void;
  onLocalUpdate?: (mailId: string, partial: { isStarred?: boolean; isTrash?: boolean; isRead?: boolean; isSpam?: boolean }) => void;
  onCompose?: (prefill: ComposePrefill, mode?: 'reply' | 'replyAll' | 'forward') => void;
}

/** 상세는 갱신 알림을 필수로 받는다 — 팝업에서 안 쓰면 빈 함수를 준다. */
const noop = () => { /* 목록에 알릴 것이 없다 */ };

const WIDTH = 560;
const HEIGHT = 440;

export function MailPopout({ mailId, title, onClose, onLocalUpdate, onCompose }: MailPopoutProps) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);

  // Esc 로 닫는다 — 상세·모달과 같은 규칙.
  useEffect(() => {
    if (!mailId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mailId, onClose]);

  const onBarPointerDown = useCallback((e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    const el = (e.currentTarget as HTMLElement).parentElement;
    if (!el) return;
    const r = el.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };

    const move = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const x = Math.max(8, Math.min(window.innerWidth - WIDTH - 8, ev.clientX - d.dx));
      const y = Math.max(8, Math.min(window.innerHeight - HEIGHT - 8, ev.clientY - d.dy));
      setPos({ x, y });
    };
    const up = () => {
      dragRef.current = null;
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  }, []);

  if (!mailId) return null;

  const style = pos
    ? { left: `${pos.x}px`, top: `${pos.y}px`, width: `${WIDTH}px`, height: `${HEIGHT}px` }
    : { width: `${WIDTH}px`, height: `${HEIGHT}px` };

  return (
    <div className={`mail-popout${pos ? ' mail-popout--moved' : ''}`} style={style} role="dialog" aria-label={tMail('mail.popout.aria')}>
      <div className="mail-popout-bar" onPointerDown={onBarPointerDown}>
        <Mail size={14} aria-hidden />
        <span className="mail-popout-title">{title}</span>
        <button
          type="button"
          className="mail-popout-close"
          onClick={onClose}
          aria-label={tMail('mail.popout.close')}
        >
          <X size={15} aria-hidden />
        </button>
      </div>
      <div className="mail-popout-body">
        <MailDetail
            mailId={mailId}
            onLocalUpdate={onLocalUpdate ?? noop}
            onCompose={onCompose}
            /* 치웠으면 이 팝업도 닫는다. ★ onClose 가 아니라 onMovedAway 로 넘긴다 —
               onClose 를 주면 상세가 자기 X 를 그려 팝업의 X 와 둘이 된다. */
            onMovedAway={onClose}
          />
      </div>
    </div>
  );
}
